import React from "react";
import {
  Paper,
  Typography,
  Stack,
  alpha,
  useTheme,
  Box,
  Skeleton,
} from "@mui/material";
import "react-datepicker/dist/react-datepicker.css";
import { formatAmount } from "../../utils/hooks/dataHandler";
import DetailsContainer from "./utils/DetailsContainer";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { TextFieldForViewOnly } from "../utils/TextField/TextField";
import YesNoSwitch from "../utils/button/YesNoSwitch";

const TaxExemptionField = ({ label, value, loading, sx }) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.2),
        bgcolor: alpha(theme.palette.background.paper, 0.8),
        flex: 1,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          borderColor: alpha(theme.palette.primary.main, 0.3),
        },
        ...sx,
      }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        gutterBottom
        sx={{
          fontWeight: 500,
          letterSpacing: "0.5px",
          textTransform: "uppercase",
          fontSize: "0.75rem",
        }}
      >
        {label}
      </Typography>
      {loading ? (
        <Skeleton width={100} height={32} />
      ) : (
        <Typography
          variant="h6"
          fontWeight={600}
          sx={{
            color: theme.palette.primary.main,
          }}
        >
          {value}
        </Typography>
      )}
    </Paper>
  );
};

const TaxExemptionDetails = ({
  id,
  dataId,
  data,
  isStartup,
  totalAmount,
  onUpdateStartup,
  disabled,
  loading,
  canEdit = true,
  isInvestmentCompany = false,
  isCrossYA = false,
  company,
}) => {
  const theme = useTheme();
  const currentYA = Number(company?.yearOfAssessment);
  const precedingYA = currentYA - 1;
  const getStartupValue = (yaKey) =>
    isCrossYA ? Boolean(isStartup?.[yaKey]) : Boolean(isStartup);
  const handleStartupChange = (yaKey, value) => {
    if (!onUpdateStartup) return;
    onUpdateStartup(yaKey, value);
  };

  const StartupToggle = ({ yaKey }) => {
    const value = getStartupValue(yaKey);

    return canEdit ? (
      <YesNoSwitch
        value={value}
        onChange={(nextValue) => handleStartupChange(yaKey, nextValue)}
        disabled={disabled}
      />
    ) : (
      <TextFieldForViewOnly value={value ? "Yes" : "No"} />
    );
  };

  const StartupQuestionBox = ({ yaKey, year }) => (
    <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={2}
      alignItems={{ xs: "flex-start", md: "center" }}
      justifyContent="space-between"
      sx={{
        mb: 3,
        p: 2,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.primary.main, 0.02),
        border: "1px solid",
        borderColor: alpha(theme.palette.primary.main, 0.1),
      }}
    >
      <Typography
        variant="body1"
        sx={{
          fontWeight: 500,
          color: "text.primary",
          flex: 1,
        }}
      >
        {year
          ? `Is this a startup company for YA ${year}?`
          : "Is this a startup company?"}
      </Typography>
      <StartupToggle yaKey={yaKey} />
    </Stack>
  );

  const renderExemptionFields = (startupValue, scopedData = data) => (
    <Stack spacing={3}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
        <TaxExemptionField
          label="% exempted from Tax"
          value="@75%"
          loading={loading}
        />
        <TaxExemptionField
          label={startupValue ? "First $100,000" : "First $10,000"}
          value={formatAmount(scopedData?.firstExemptAmount)}
          loading={loading}
        />
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
        <TaxExemptionField
          label="% exempted from Tax"
          value="@50%"
          loading={loading}
        />
        <TaxExemptionField
          label={startupValue ? "Next $100,000" : "Next $190,000"}
          value={formatAmount(scopedData?.secondExemptAmount)}
          loading={loading}
        />
      </Stack>
    </Stack>
  );

  const renderYACard = (yaKey, label) => (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.18),
        bgcolor: "background.paper",
        flex: 1,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          mb: 2,
          fontWeight: 700,
          color: "text.primary",
        }}
      >
        {label}
      </Typography>
      <StartupQuestionBox
        yaKey={yaKey}
        year={yaKey === "preceding" ? precedingYA : currentYA}
      />
      {renderExemptionFields(getStartupValue(yaKey), data?.[yaKey])}
    </Paper>
  );

  return (
    <DetailsContainer
      id={id}
      title="Partial Tax Exemption / Full Tax Exemption"
      totalAmount={totalAmount}
      icon={VerifiedUserIcon}
      loading={loading}
    >
      <Box sx={{ p: 3 }}>
        {!isInvestmentCompany && !isCrossYA && <StartupQuestionBox />}

        {isCrossYA && !isInvestmentCompany ? (
          <Stack direction={{ xs: "column", lg: "row" }} spacing={3}>
            {renderYACard("preceding", `Preceding YA ${precedingYA}`)}
            {renderYACard("current", `Current YA ${currentYA}`)}
          </Stack>
        ) : (
          renderExemptionFields(getStartupValue("current"))
        )}
      </Box>
    </DetailsContainer>
  );
};

export default TaxExemptionDetails;
